import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useIsMobile } from '../hooks/useMediaQuery';

// ── Footer ────────────────────────────────────────────────────────────────────
export default function Footer() {
    const [email, setEmail] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const year = new Date().getFullYear();
    const isMobile = useIsMobile();
    // Mobile (2026-10-28): merge the Smobile override in only below 768px.
    const m = (key) => (isMobile ? { ...F[key], ...(Fmobile[key] || {}) } : F[key]);

    const handleSignUp = (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        // TODO: wire to email list Lambda / third-party service
        console.log('Email signup:', email);
        setSubmitted(true);
        setEmail('');
    };

    return (
        <footer style={F.footer}>
            <div style={F.inner}>

                {/* ── Top row: four columns (mobile: three link columns,
                     email signup on its own full-width row underneath) ── */}
                <div style={m('columns')}>

                    {/* Overview */}
                    <div style={m('col')}>
                        <p style={m('colHeading')}>Overview</p>
                        <FooterLink to="/how-it-works/fans">For Fans</FooterLink>
                        <FooterLink to="/how-it-works/artists">For Artists</FooterLink>
                        <FooterLink to="/how-it-works/venues">For Venues</FooterLink>
                        <FooterLink to="/how-it-works/promoters">For Promoters</FooterLink>
                        <FooterLink to="/how-it-works">Get Verified</FooterLink>
                        <FooterLink to="/projects">Projects &amp; Community</FooterLink>
                    </div>

                    {/* Company */}
                    <div style={m('col')}>
                        <p style={m('colHeading')}>Company</p>
                        <FooterLink to="/about">About sharc.fm</FooterLink>
                        <FooterLink to="/contact">Contact Us</FooterLink>
                        <FooterLink to="/faq">FAQ</FooterLink>
                        <FooterLink to="/guidelines">Guidelines</FooterLink>
                        <FooterLink to="/careers">Careers</FooterLink>
                    </div>

                    {/* Follow Us */}
                    <div style={m('col')}>
                        <p style={m('colHeading')}>Follow Us</p>
                        <FooterExternalLink href="https://facebook.com/sharcfm">Facebook</FooterExternalLink>
                        <FooterExternalLink href="https://instagram.com/sharcfm">Instagram</FooterExternalLink>
                        <FooterExternalLink href="https://youtube.com/@sharcfm">YouTube</FooterExternalLink>
                        <FooterExternalLink href="https://x.com/sharcfm">X</FooterExternalLink>
                        <FooterExternalLink href="https://tiktok.com/@sharcfm">TikTok</FooterExternalLink>
                        <FooterExternalLink href="https://linkedin.com/company/sharcfm">LinkedIn</FooterExternalLink>
                    </div>

                    {/* Email signup */}
                    <div style={isMobile ? { ...F.col, ...Fmobile.signupCol } : F.col}>
                        {submitted ? (
                            <p style={F.signupThanks}>Thanks — you're in! 🎶</p>
                        ) : (
                            <form onSubmit={handleSignUp} style={F.signupForm}>
                                <input
                                    type="email"
                                    placeholder="Enter email address"
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    style={F.signupInput}
                                    required
                                />
                                <button type="submit" style={F.signupBtn}>Sign Up</button>
                                <p style={F.signupDisclaimer}>
                                    By entering my email address, I consent to receive
                                    communications about music, promotions, and sharc.fm.
                                    Unsubscribe at any time using the "unsubscribe" link in
                                    the emails. Learn more about your rights and how sharc.fm
                                    handles your personal data by reviewing the{' '}
                                    <Link to="/privacy" style={F.signupDisclaimerLink}>
                                        Privacy Policy
                                    </Link>.
                                </p>
                            </form>
                        )}
                    </div>

                </div>

                {/* ── Divider ── */}
                <div style={F.divider} />

                {/* ── Bottom bar ── */}
                <div style={F.bottomBar}>
                    <p style={F.copyright}>
                        © {year} sharc.fm · All Rights Reserved
                    </p>
                    <div style={F.legalLinks}>
                        <FooterLegalLink to="/terms">Terms of Service</FooterLegalLink>
                        <span style={F.legalDot}>·</span>
                        <FooterLegalLink to="/privacy">Privacy Policy</FooterLegalLink>
                        <span style={F.legalDot}>·</span>
                        <FooterLegalLink to="/cookies">Cookie Policy</FooterLegalLink>
                    </div>
                </div>

            </div>
        </footer>
    );
}

// ── Small helper components ───────────────────────────────────────────────────

function FooterLink({ to, children }) {
    return (
        <Link to={to} style={useLinkStyle()}>
            {children}
        </Link>
    );
}

function FooterExternalLink({ href, children }) {
    return (
        <a href={href} target="_blank" rel="noopener noreferrer" style={useLinkStyle()}>
            {children}
        </a>
    );
}

// Column link style, with the mobile override merged in below 768px.
function useLinkStyle() {
    const isMobile = useIsMobile();
    return isMobile ? { ...F.link, ...Fmobile.link } : F.link;
}

function FooterLegalLink({ to, children }) {
    return (
        <Link to={to} style={F.legalLink}>
            {children}
        </Link>
    );
}

// ── Footer styles (F) ─────────────────────────────────────────────────────────
const F = {
    footer: {
        backgroundColor: '#0a0a0a',
        borderTop: '1px solid #2a2a2a',
        marginTop: 'auto',
        width: '100%',
    },
    inner: {
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '48px 24px 24px',
    },
    columns: {
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '40px',
    },
    col: {
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
    },
    colHeading: {
        fontSize: '13px',
        fontWeight: '600',
        color: '#e0e0e0',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        marginBottom: '4px',
    },
    link: {
        fontSize: '14px',
        color: '#888',
        textDecoration: 'none',
        lineHeight: '1.4',
        transition: 'color 0.15s',
    },
    signupForm: {
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
    },
    signupInput: {
        backgroundColor: '#1e1e1e',
        border: '1px solid #333',
        borderRadius: '6px',
        color: '#e0e0e0',
        fontSize: '14px',
        padding: '10px 12px',
        width: '100%',
        outline: 'none',
    },
    signupBtn: {
        backgroundColor: '#bb86fc',
        border: 'none',
        borderRadius: '6px',
        color: '#121212',
        fontSize: '14px',
        fontWeight: '600',
        padding: '10px',
        cursor: 'pointer',
        width: '100%',
    },
    signupThanks: {
        fontSize: '14px',
        color: '#bb86fc',
        marginTop: '8px',
    },
    signupDisclaimer: {
        fontSize: '11px',
        color: '#555',
        lineHeight: '1.5',
    },
    signupDisclaimerLink: {
        color: '#777',
        textDecoration: 'underline',
    },
    divider: {
        height: '1px',
        backgroundColor: '#2a2a2a',
        margin: '36px 0 20px',
    },
    bottomBar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '12px',
    },
    copyright: {
        fontSize: '12px',
        color: '#555',
    },
    legalLinks: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flexWrap: 'wrap',
    },
    legalLink: {
        fontSize: '12px',
        color: '#555',
        textDecoration: 'none',
    },
    legalDot: {
        fontSize: '12px',
        color: '#333',
    },
};

// ── Mobile overrides (Fmobile, 2026-10-28) ────────────────────────────────────
// Merged over F only when useIsMobile() is true (same pattern as the per-page
// Smobile objects). Below 768px the four-column grid becomes three equal
// link columns (Overview / Company / Follow Us), top-aligned and
// left-justified, with the email signup moved to a full-width row beneath.
const Fmobile = {
    columns: {
        // minmax(0, 1fr) keeps the three columns exactly equal — plain 1fr
        // lets a long link ("Projects & Community") widen its column.
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: '28px 16px',
        alignItems: 'start',
    },
    col: {
        minWidth: 0,
        alignItems: 'flex-start',
        textAlign: 'left',
        gap: '8px',
    },
    colHeading: {
        fontSize: '12px',
        letterSpacing: '0.04em',
        whiteSpace: 'nowrap',
    },
    link: {
        fontSize: '13px',
        overflowWrap: 'anywhere',
    },
    // Email signup spans all three columns, separated from the links above.
    signupCol: {
        gridColumn: '1 / -1',
        borderTop: '1px solid #2a2a2a',
        paddingTop: '24px',
    },
};
